import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  signal,
  viewChild,
  ViewEncapsulation
} from '@angular/core';
import {concatMap, debounceTime, skip, take} from 'rxjs/operators';
import {AudioWaveformPlayerComponent} from "../../media-players/audio-waveform-player/audio-waveform-player.component";
import {CurrentObjectContext} from "../current-object.context";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {ActivatedRoute, Router} from "@angular/router";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MatProgressBar} from "@angular/material/progress-bar";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButton} from "@angular/material/button";
import {CmsApiService} from "../../core/cms-api.service";
import {ObjectStorageService} from "../../core/object-storage.service";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {MatIcon} from "@angular/material/icon";
import {MediaHelperService} from "../../core/media-helper.service";
import {RecordingSegmentTableComponent} from "./recording-segment-table/recording-segment-table.component";
import {RecordingSegmentFormComponent} from "./recording-segment-form/recording-segment-form.component";
import {createEmptyRecordingSegment, RecordingSegmentView} from "./recording-segment.types";
import {Region, RegionParams} from "wavesurfer.js/plugins/regions";
import {SuperObjectModel} from "../../core/definitions/super-object-model";
import {Recording} from "../../core/definitions/recording";
import {firstValueFrom} from "rxjs";

@Component({
  selector: 'app-recording-segmenter',
  imports: [
    AudioWaveformPlayerComponent,
    MatProgressBar,
    FormsModule,
    TranslatePipe,
    ReactiveFormsModule,
    MatButton,
    MatMenu,
    MatIcon,
    MatMenuTrigger,
    MatMenuItem,
    RecordingSegmentTableComponent,
    RecordingSegmentFormComponent,

  ],
  templateUrl: './object-page-v2-recording-segments.component.html',
  styleUrl: './object-page-v2-recording-segments.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ObjectPageV2RecordingSegmentsComponent implements OnDestroy {
  // Injections
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly mediaHelper: MediaHelperService = inject(MediaHelperService);
  private readonly currentObjectContext: CurrentObjectContext = inject(CurrentObjectContext);
  private readonly objectStorageService = inject(ObjectStorageService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private preparingCount = 0;
  private readonly isPreparing = signal(true);
  private initialized = false;
  private playbackUrlRefreshed = false;
  private readonly waveformLoadFailed = signal(false);

  player = viewChild(AudioWaveformPlayerComponent);

  selectedMediaElement = signal<any>(null);
  segmentObjects = signal<any[]>([]);
  recordingSegments = signal<RecordingSegmentView[]>([]);
  isPlaying = signal<boolean>(false);
  segmentIdsAtCursor = signal<string[]>([]);
  // Set while a segment is played from its table row, so only that row is active
  playingSegmentId = signal<string | null>(null);
  activeSegmentIds = computed(() => {
    const playingId = this.playingSegmentId();
    return playingId ? [playingId] : this.segmentIdsAtCursor();
  });
  playerSegmentRegions = signal<RegionParams[]>([]);
  draftSegment = signal<RecordingSegmentView | null>(null);
  isSubmittingSegment = signal(false);

  mediaPlaybackUrl = signal<string | undefined>(undefined);
  // The media is still being uploaded or converted, so there is nothing to play yet
  mediaNotReady = signal(false);

  private readonly isWaveformLoading = computed(() =>
    !!this.mediaPlaybackUrl() && !this.waveformLoadFailed() && !this.player()?.isLoaded()
  );

  draftRegion = computed<RegionParams | null>(() => {
    const segment = this.draftSegment();

    if (!segment) {
      return null;
    }

    const durationSec = this.player()?.duration?.() ?? Infinity;
    const maxMs = Number.isFinite(durationSec)
      ? Math.round(durationSec * 1000)
      : Number.MAX_SAFE_INTEGER;

    const clampMs = (value: number) => Math.max(0, Math.min(value ?? 0, maxMs));
    const startMs = clampMs(segment.start ?? 0);
    const endMs = clampMs(segment.end ?? 0);

    if (startMs >= endMs) {
      return null;
    }

    return {
      start: startMs / 1000,
      end: endMs / 1000,
      content: segment.remarks || this.translate.instant('TRANS__CREATE_NEW__RECORDING_SEGMENT_HEADLINE'),
    };
  });

  constructor() {
    this.currentObjectContext.objectLoaded$
      .pipe(
        debounceTime(0),
        // One run at a time, so an older object load cannot overwrite a newer one
        concatMap(() => this.initialize().catch(error =>
          console.error('Failed to initialize recording segments.', error))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    effect(() => {
      const region = this.draftRegion();

      if (!region) {
        return;
      }
      const start = Math.round(region.start * 1000);
      const end = Math.round(region.end * 1000);

      this.draftSegment.update(current => {
        if (!current || (current.start === start && current.end === end)) {
          return current;
        }
        return {...current, start, end};
      });
    });
  }

  get artifact() {
    return this.currentObjectContext.art;
  }

  get loading() {
    return this.currentObjectContext.loadingArtifact;
  }

  get busy(): boolean {
    return this.isLoadingPage || this.isSubmittingSegment();
  }

  // The form keeps its own readonly state while a segment is submitted
  get segmentFormDisabled(): boolean {
    return this.isLoadingPage || this.mediaNotReady();
  }

  private get isLoadingPage(): boolean {
    return this.loading
      || this.isPreparing()
      || this.isWaveformLoading();
  }

  get mediaContainer() {
    return this.artifact?.$$mediaContainer;
  }

  get mediaElements() {
    return this.mediaContainer?.mediaTypes
        ?.filter(mt => mt.mediaType === 'Audio' || mt.mediaType === 'Video')
        .flatMap(mt => mt.mediaElements ?? [])
        .filter(element => !element.$$segmentOnly)
      ?? [];
  }

  ngOnDestroy(): void {
    this.currentObjectContext.pageTitle = null;
  }

  async selectMediaElement(element: any) {
    if (!element) return;
    await this.whilePreparing(async () => {
      // Fetch media playback URL if not set
      let uploadStatus = 'done';
      if (!element.mediaObject?.$$mediaPlaybackUrls?.length) {
        uploadStatus = await this.mediaHelper.getMediaUploadStatus(element.mediaObject);
        if (uploadStatus === 'done') {
          element.mediaObject.$$mediaPlaybackUrls =
            await this.mediaHelper.getMediaPlaybackUrls(element.mediaObject);
        }
      }
      this.mediaNotReady.set(uploadStatus !== 'done');
      this.selectedMediaElement.set(element);
      this.draftSegment.set(null);
      this.playbackUrlRefreshed = false;
      this.setPlaybackUrl(this.mediaHelper.getWaveformPlaybackUrl(element.mediaObject));
      this.updateSegments();
    });
  }

  // A cached playback url may have expired, so order fresh urls once before giving up
  async onPlayerLoadFailed(failedUrl: string) {
    const element = this.selectedMediaElement();
    if (this.playbackUrlRefreshed || !element) {
      this.waveformLoadFailed.set(true);
      return;
    }
    this.playbackUrlRefreshed = true;

    await this.whilePreparing(async () => {
      element.mediaObject.$$mediaPlaybackUrls = await this.mediaHelper.getMediaPlaybackUrls(element.mediaObject);
      const url = this.mediaHelper.getWaveformPlaybackUrl(element.mediaObject);
      if (!url || url === failedUrl) {
        this.waveformLoadFailed.set(true);
      } else {
        this.setPlaybackUrl(url);
      }
    });
  }

  async onSegmentFormSubmitted(draft: RecordingSegmentView) {
    if (this.isSubmittingSegment()) {
      return;
    }

    const parent = this.artifact;
    const selectedMedia = this.selectedMediaElement();
    const sourceMediaId = selectedMedia?.mediaObject?.artifact_id;

    if (!parent || !sourceMediaId) {
      console.error('Cannot create recording segment: missing parent artifact or media.');
      return;
    }

    this.isSubmittingSegment.set(true);

    try {
      const copyData: SuperObjectModel = await this.cms.copyArtifact({
        artifact_id: parent.artifact_id,
        object_type: parent.object_type,
      });

      const segment = new Recording();
      // @ts-ignore
      segment.reg_level = copyData.reg_level;
      segment.collection = copyData.collection;
      segment.object_type = parent.object_type;
      segment.superobject_type_id = parent.superobject_type_id;
      segment.superobject_type_id_value = parent.superobject_type_id_value;
      segment.identification_number = {
        auto_generated: false,
        identification_number: '_IGNORE_',
        context_id: parent.artifact_id,
      };
      segment.recording_type = {
        type_id: 'ct_175-49f80624-d8c7-4690-bfbe-d8ec8f766192',
      };
      segment.recording_segment = {
        source_recording_id: parent.artifact_id,
        source_media_id: sourceMediaId,
        start: draft.start,
        end: draft.end,
        remarks: draft.remarks ?? '',
      };

      const storeResult = await this.objectStorageService.storeObject(
        this.objectStorageService.getCleanObject(segment)
      );

      // Update parent/child relations and refresh the parent context
      parent.children = [
        ...(parent.children ?? []),
        {
          object_type: 'ObjectChildItem',
          relation_id: storeResult.artifact_id,
        },
      ];

      await this.objectStorageService.storeObject(
        this.objectStorageService.getCleanObject(parent)
      );

      const nextObjectLoaded = firstValueFrom(
        this.currentObjectContext.objectLoaded$.pipe(skip(1), take(1))
      );
      this.currentObjectContext.objectRefresh({});
      await nextObjectLoaded;
    } catch (error) {
      console.error('Failed to create recording segment.', error);
    } finally {
      this.draftSegment.set(null);
      this.isSubmittingSegment.set(false);
    }
  }

  onSegmentFormCleared(): void {
    if (!this.isSubmittingSegment()) {
      this.draftSegment.set(null);
    }
  }

  onPlayerPlaying(playing: boolean) {
    this.isPlaying.set(playing);
  }

  onRegionsAtCursor(regionIds: string[]): void {
    this.segmentIdsAtCursor.set(regionIds);
  }

  onPlayerControlUsed(): void {
    this.playingSegmentId.set(null);
  }

  onSegmentPlayClicked(segmentId: string): void {
    if (this.activeSegmentIds().includes(segmentId)) {
      this.player()?.togglePlay(segmentId);
    } else {
      this.player()?.play(segmentId);
    }
    this.playingSegmentId.set(this.hasTimeRange(segmentId) ? segmentId : null);
  }

  private hasTimeRange(segmentId: string): boolean {
    const segment = this.recordingSegments().find(s => s.artifact_id === segmentId);
    return !!segment && segment.start !== segment.end;
  }

  onPlayerRegionUpdated(region: Region): void {
    if (region.id !== 'draft-region') {
      return;
    }

    this.draftSegment.update(current => ({
      ...(current ?? createEmptyRecordingSegment()),
      start: Math.round((region.start ?? 0) * 1000),
      end: Math.round((region.end ?? 0) * 1000),
    }));
  }

  private async initialize() {
    await this.whilePreparing(async () => {
      if (!this.initialized) {
        if (!this.currentObjectContext.operationContainer?.currentOperation) {
          const operation = this.currentObjectContext.operationContainer?.operations
            ?.find(op => op.operation_type_id === '3bfdc1be-69e6-4cde-9a23-b3eca15826c2');
          if (operation) {
            this.currentObjectContext.setCurrentOperation(operation);
          } else {
            await this.router.navigate(['../overview'], {relativeTo: this.route});
            return;
          }
        }
      }

      this.currentObjectContext.pageTitle = [
        this.translate.instant('TRANS__HEADER__RECORDING__SPLIT'),
        this.currentObjectContext.art.artifact_name,
      ].join(' ');

      if (this.mediaElements.length === 0) return;

      // Always reload segments from the artifact
      const recording = this.artifact as Recording;
      const segments = recording?.recording_segments ?? [];

      if (segments.length > 0) {
        const childIds = segments.map(s => s.context_id);
        this.segmentObjects.set(await this.objectStorageService.loadObjects(childIds));
      } else {
        this.segmentObjects.set([]);
      }

      if (!this.initialized) {
        // The object may have been reloaded while the segments loaded, leaving no media yet
        const element = this.mediaElements[0];
        if (!this.selectedMediaElement() && !element) return;
        this.initialized = true;
        if (!this.selectedMediaElement()) {
          await this.selectMediaElement(element);
        }
      } else {
        this.updateSegments();
      }
    });
  }

  private setPlaybackUrl(url: string | undefined) {
    this.waveformLoadFailed.set(false);
    this.mediaPlaybackUrl.set(url);
  }

  private updateSegments() {
    const artifactId = this.artifact?.artifact_id;
    const mediaId = this.selectedMediaElement()?.mediaObject?.artifact_id;
    if (!artifactId || !mediaId) return;

    const filtered: RecordingSegmentView[] = this.segmentObjects()
      .filter(s => {
        const seg = s?.recording_segment;
        return seg
          && seg.source_recording_id === artifactId
          && seg.source_media_id === mediaId
          && seg.start != null
          && seg.end != null;
      })
      .map(s => ({
        artifact_id: s.artifact_id,
        artifact_name: s.artifact_name,
        source_recording_id: s.recording_segment.source_recording_id,
        source_media_id: s.recording_segment.source_media_id,
        start: s.recording_segment.start,
        end: s.recording_segment.end,
        remarks: s.recording_segment.remarks,
        segment_order: s.recording_segment.segment_order,
      }));

    this.recordingSegments.set(filtered);
    this.playerSegmentRegions.set(filtered.map(s => ({
      id: s.artifact_id,
      content: s.remarks,
      start: parseFloat((s.start / 1000).toFixed(2)),
      end: parseFloat((s.end / 1000).toFixed(2)),
    })));
  }

  private async whilePreparing<T>(work: () => Promise<T>): Promise<T> {
    this.preparingCount++;
    this.isPreparing.set(true);
    try {
      return await work();
    } finally {
      if (--this.preparingCount === 0) {
        this.isPreparing.set(false);
      }
    }
  }
}
